import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, TextField, Button, alpha, useTheme } from "@mui/material";
import { FormatColorTextOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updatePrompt } from "../../../../redux/actions/userAction";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { tracker } from "../../../../analytics";
import SectionHeader from "../SectionHeader/SectionHeader";

const PromptsTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const user = useSelector((state) => state.auth?.user?.user);
  const organization_prompt = useSelector((state) => state.user?.organization_prompt);

  const [promptText, setPromptText] = useState("");
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);

  useEffect(() => {
    setPromptText(organization_prompt?.prompt_text);
  }, [organization_prompt]);

  const handlePromptChange = (event) => {
    setPromptText(event.target.value);
  };

  const handleSavePrompt = async () => {
    setIsSavingPrompt(true);
    try {
      await dispatch(updatePrompt(user.id, "organization_prompt", promptText));
      enqueueSnackbar(t("prompt_updated_success"), { variant: "success" });

      ReactGA.event({
        category: "Settings",
        action: "Saved Prompt",
        label: `User ID: ${user.id}`,
      });
      tracker.event({
        category: "Settings",
        action: "Saved Prompt",
        label: `User ID: ${user.id}`,
      });
    } catch (error) {
      console.error("Failed to update prompt:", error);
      enqueueSnackbar(t("error_saving"), { variant: "error" });
    }
    setIsSavingPrompt(false);
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 4,
        borderRadius: "16px",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <SectionHeader
          icon={<FormatColorTextOutlined />}
          title={t("prompt_organization")}
          description={t("prompt_description")}
        />

        <TextField
          data-tour="prompt-text-field"
          label={t("prompt_organization")}
          multiline
          rows={8}
          variant="outlined"
          fullWidth
          value={promptText || ""}
          onChange={handlePromptChange}
          InputProps={{
            sx: {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.divider,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.dark,
              },
            },
          }}
          sx={{ mb: 4 }}
        />

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSavePrompt}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.mode === "dark" ? "#000" : "#fff",
              px: 4,
              py: 1.5,
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { bgcolor: theme.palette.primary.dark },
            }}
          >
            {isSavingPrompt ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    border: "3px solid rgba(255,255,255,0.3)",
                    borderTopColor: "#fff",
                    animation: "spin 1s linear infinite",
                    marginRight: 8,
                  }}
                ></span>
                {t("saving")}
              </Box>
            ) : (
              t("save_prompt")
            )}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PromptsTab;
